import React, { useRef, useEffect, useState } from 'react';
import { Activity, Radio, Music, Zap, Sliders, MapPin, Gauge, Sparkles, AlertTriangle, ShieldAlert } from 'lucide-react';
import { AudioEngine } from '../audio/AudioEngine';

interface DynamicSonificationDashboardProps {
  activeMovementIdx: number;
  figId: string;
  progress: number;
  isPlaying: boolean;
  onScrubProgress: (newProgress: number) => void;
}

interface DashboardTelemetry {
  title: string;
  yearLabel: string;
  metricName: string;
  metricValue: string;
  synthPatches: string[];
  scaleMode: string;
  tempAnomaly: number;
  freqHz: number;
  noteName: string;
}

const getDashboardTelemetry = (figId: string, progress: number): DashboardTelemetry => {
  const t = Math.max(0, Math.min(1, progress));
  const tempAnomaly = parseFloat((-0.2 + t * 4.2).toFixed(2));
  const freqHz = Math.round(261.63 * Math.pow(2, (tempAnomaly * 4.8) / 12));

  const notes = ['C4', 'C#4', 'D4', 'D#4', 'E4', 'F4', 'F#4', 'G4', 'G#4', 'A4', 'A#4', 'B4', 'C5', 'D5', 'E5'];
  const noteIdx = Math.min(notes.length - 1, Math.max(0, Math.floor(((freqHz - 261) / 300) * notes.length)));
  const noteName = notes[noteIdx] ?? 'C4';

  let scaleMode = 'C Lydian (Stable Baseline)';
  if (tempAnomaly >= 2.8) scaleMode = 'C Locrian (Extreme Dissonance)';
  else if (tempAnomaly >= 1.8) scaleMode = 'C Dorian (Somber Minor)';
  else if (tempAnomaly >= 0.8) scaleMode = 'C Mixolydian (Suspensive)';

  switch (figId) {
    case 'spm1': {
      const year = Math.round(1850 + t * 170);
      const temp = (-0.2 + Math.pow(t, 1.4) * 1.5).toFixed(2);
      return {
        title: 'SPM.1 • Historical Warming Trajectory (1850–2020)',
        yearLabel: `Year ${year}`,
        metricName: 'Observed Global Temperature Anomaly',
        metricValue: `+${temp}°C`,
        synthPatches: ['🎻 Nat-Warmth (Dual-Triangle Pad)', '🎹 Anthropo-Pulse (FM Formant)'],
        scaleMode,
        tempAnomaly: parseFloat(temp),
        freqHz,
        noteName
      };
    }
    case 'spm2': {
      const forcing = (t * 1.3).toFixed(2);
      return {
        title: 'SPM.2 • Radiative Forcing Drivers (Greenhouse Gases)',
        yearLabel: 'Human Driver Component',
        metricName: 'Net Radiative Forcing Impact',
        metricValue: `+${forcing} W/m²`,
        synthPatches: ['🎹 Anthropo-Pulse (FM Sweep)', '💨 Aerosol-Shield (Bandpass Noise)'],
        scaleMode,
        tempAnomaly,
        freqHz,
        noteName
      };
    }
    case 'spm3': {
      const regions = ['WNA (North America)', 'NEU (North Europe)', 'EAS (East Asia)', 'SAF (South Africa)', 'AUS (Australia)'];
      const region = regions[Math.floor(t * 4.9)];
      return {
        title: 'SPM.3 • Regional Weather Extremes Scan',
        yearLabel: region,
        metricName: 'Extreme Heat Confidence Level',
        metricValue: 'High Confidence Increase',
        synthPatches: ['🥁 Extreme-Transient (Percussive Hit)', '💨 Aerosol Noise'],
        scaleMode,
        tempAnomaly,
        freqHz,
        noteName
      };
    }
    case 'spm9': {
      const cids = ['Heat & Cold', 'Wet & Dry', 'Wind & Storms', 'Coastal & Oceanic', 'Snow & Ice'];
      const cid = cids[Math.floor(t * 4.9)];
      return {
        title: 'SPM.9 • Climate Impact-Driver (CID) Risk Matrix',
        yearLabel: cid,
        metricName: 'CID Regional Risk Exposure',
        metricValue: 'Multi-Driver Risk Active',
        synthPatches: ['💨 Aerosol-Shield (Dual Bandpass)', '🥁 Percussive Layer'],
        scaleMode,
        tempAnomaly,
        freqHz,
        noteName
      };
    }
    case 'spm4': {
      const year = Math.round(2015 + t * 85);
      const ssp585 = (1.4 + t * 3.0).toFixed(2);
      return {
        title: 'SPM.4 • Future Scenario Projections (2015–2100)',
        yearLabel: `Year ${year}`,
        metricName: 'SSP5-8.5 High-Emissions Anomaly',
        metricValue: `+${ssp585}°C`,
        synthPatches: ['🎼 Polyphonic 5-SSP Scenario Divergence Layer'],
        scaleMode,
        tempAnomaly: parseFloat(ssp585),
        freqHz,
        noteName
      };
    }
    case 'spm5': {
      const lat = Math.round(-80 + t * 160);
      const localWarm = (1.0 + Math.pow(t, 1.5) * 3.2).toFixed(2);
      return {
        title: 'SPM.5 • Spatial Latitude Warming Pattern',
        yearLabel: `Latitude ${lat > 0 ? `${lat}°N` : `${Math.abs(lat)}°S`}`,
        metricName: 'Local Surface Warming Level',
        metricValue: `+${localWarm}°C (Polar Amplification)`,
        synthPatches: ['🌐 Spatial Latitude Polar Sweep Filter'],
        scaleMode,
        tempAnomaly: parseFloat(localWarm),
        freqHz,
        noteName
      };
    }
    case 'spm6': {
      const co2 = Math.round(t * 4500);
      const temp = (t * 3.5).toFixed(2);
      return {
        title: 'SPM.6 • Cumulative CO2 Emissions Runway',
        yearLabel: `${co2} GtCO2`,
        metricName: 'Linear Temperature Response (TCRE)',
        metricValue: `+${temp}°C`,
        synthPatches: ['⏱️ Carbon Metronome (100 GtCO2 Pulse)', '💨 Noise Layer'],
        scaleMode,
        tempAnomaly: parseFloat(temp),
        freqHz,
        noteName
      };
    }
    case 'spm7': {
      const mult = (1 + Math.pow(t, 2.5) * 38.2).toFixed(1);
      return {
        title: 'SPM.7 • Extreme Weather Frequency Accelerations',
        yearLabel: '50-Year Extreme Heat',
        metricName: 'Return Frequency Multiplier',
        metricValue: `${mult}x Pre-Industrial Rate`,
        synthPatches: ['🚨 Extreme Frequency Multipliers (Up to 39.2x)'],
        scaleMode,
        tempAnomaly,
        freqHz,
        noteName
      };
    }
    case 'spm8': {
      const year = Math.round(2020 + t * 280);
      const slr = (t * 3.5).toFixed(2);
      return {
        title: 'SPM.8 • Multi-Century Sea Level Rise (2020–2300)',
        yearLabel: `Year ${year}`,
        metricName: 'Global Mean Sea Level Commitment',
        metricValue: `+${slr} meters`,
        synthPatches: ['🌊 Abyssal-Drone (Phantom Sub-Bass)', '❄️ Cryo-Hiss'],
        scaleMode,
        tempAnomaly,
        freqHz,
        noteName
      };
    }
    case 'spm10': {
      const budgetLeft = Math.max(0, Math.round(500 * (1 - t)));
      return {
        title: 'SPM.10 • Remaining Carbon Budget Allowance',
        yearLabel: '1.5°C Paris Budget',
        metricName: 'Remaining Carbon Allowance',
        metricValue: `${budgetLeft} GtCO2 Left`,
        synthPatches: ['❄️ Cryo-Hiss & Low-Pass Budget Allowance Filter'],
        scaleMode,
        tempAnomaly,
        freqHz,
        noteName
      };
    }
    default:
      return {
        title: 'IPCC SPM Data Sonification',
        yearLabel: 'Timeline',
        metricName: 'Warming Anomaly',
        metricValue: '+1.1°C',
        synthPatches: ['Nat-Warmth'],
        scaleMode: 'C Lydian',
        tempAnomaly: 1.1,
        freqHz: 261,
        noteName: 'C4'
      };
  }
};

export const DynamicSonificationDashboard: React.FC<DynamicSonificationDashboardProps> = ({
  activeMovementIdx,
  figId,
  progress,
  isPlaying,
  onScrubProgress
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const telemetry = getDashboardTelemetry(figId, progress);
  const [lastChimeThreshold, setLastChimeThreshold] = useState<number>(0);
  const [showThresholdLines, setShowThresholdLines] = useState<boolean>(false);

  // Trigger Tibetan acoustic warning chime on threshold crossings (Graduated Escalation)
  useEffect(() => {
    if (!isPlaying) return;
    const temp = telemetry.tempAnomaly;
    let threshold = 0;
    if (temp >= 4.0) threshold = 4;
    else if (temp >= 2.0) threshold = 3;
    else if (temp >= 1.5) threshold = 2;
    else if (temp >= 1.0) threshold = 1;

    if (threshold > 0 && threshold !== lastChimeThreshold) {
      setLastChimeThreshold(threshold);
      AudioEngine.getInstance().playWarningChime(392, temp);
    }
  }, [telemetry.tempAnomaly, isPlaying, lastChimeThreshold]);

  // Dynamic Live Trajectory Graph Canvas with Horizontal Threshold Lines
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderGraph = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // Background Grid Lines
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let y = 20; y < h; y += 25) {
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      // --- CLIMATE WARNING THRESHOLD HORIZONTAL LINES (TOGGLEABLE) ---
      if (showThresholdLines) {
        const thresholds = [
          { label: '1.0°C Baseline', temp: 1.0, color: '#f59e0b', opacity: '0.4' },
          { label: '1.5°C Paris Limit', temp: 1.5, color: '#f97316', opacity: '0.5' },
          { label: '2.0°C Severe Limit', temp: 2.0, color: '#f43f5e', opacity: '0.6' },
          { label: '4.0°C Extreme Limit', temp: 4.0, color: '#a855f7', opacity: '0.5' }
        ];

        thresholds.forEach((th) => {
          const thY = h - (0.15 + (th.temp / 4.5) * 0.7) * h;
          if (thY >= 10 && thY <= h - 10) {
            ctx.save();
            ctx.setLineDash([3, 5]);
            ctx.strokeStyle = th.color;
            ctx.globalAlpha = parseFloat(th.opacity);
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(0, thY);
            ctx.lineTo(w - 110, thY);
            ctx.stroke();

            // Sleek Minimalist Badge Tag on Far Right Margin
            ctx.globalAlpha = 0.85;
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(w - 105, thY - 8, 100, 14);
            ctx.strokeStyle = th.color;
            ctx.lineWidth = 0.5;
            ctx.strokeRect(w - 105, thY - 8, 100, 14);

            ctx.fillStyle = th.color;
            ctx.font = 'bold 8px monospace';
            ctx.fillText(th.label, w - 100, thY + 2);
            ctx.restore();
          }
        });
      }

      // --- DYNAMIC MULTI-THRESHOLD COLOR-SHIFTING TRAJECTORY LINE ---
      // Line color shifts dynamically: Cyan (<1.0°C) -> Amber (1.0°C) -> Orange (1.5°C) -> Red (2.0°C) -> Purple (4.0°C+)
      const getThresholdColor = (normX: number): string => {
        const temp = -0.2 + normX * 4.2;
        if (temp >= 4.0) return '#a855f7'; // Purple (Catastrophic)
        if (temp >= 2.0) return '#f43f5e'; // Red (Severe 2.0°C)
        if (temp >= 1.5) return '#f97316'; // Orange (Paris 1.5°C)
        if (temp >= 1.0) return '#f59e0b'; // Amber (1.0°C Baseline)
        return '#38bdf8'; // Cyan (Serene Baseline)
      };

      // Filled Trajectory Area with Dynamic Color Gradient
      const playX = progress * w;
      const currentLineColor = getThresholdColor(progress);

      const gradient = ctx.createLinearGradient(0, 0, 0, h);
      gradient.addColorStop(0, `${currentLineColor}55`);
      gradient.addColorStop(1, `${currentLineColor}00`);

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(0, h);

      for (let x = 0; x <= playX; x += 4) {
        const normX = x / w;
        const normY = h - (0.15 + Math.pow(normX, 1.4) * 0.7) * h;
        ctx.lineTo(x, normY);
      }

      ctx.lineTo(playX, h);
      ctx.closePath();
      ctx.fill();

      // Plotted Line Segment Gradient Shifting
      for (let x = 0; x < playX; x += 4) {
        const x1 = x;
        const x2 = Math.min(playX, x + 4);
        const normX1 = x1 / w;
        const normX2 = x2 / w;

        const y1 = h - (0.15 + Math.pow(normX1, 1.4) * 0.7) * h;
        const y2 = h - (0.15 + Math.pow(normX2, 1.4) * 0.7) * h;

        ctx.strokeStyle = getThresholdColor(normX2);
        ctx.lineWidth = 3.5;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Current Acoustic Node Dot with Matching Color Halo
      if (isPlaying && playX > 0) {
        const currentY = h - (0.15 + Math.pow(progress, 1.4) * 0.7) * h;
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = currentLineColor;
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.arc(playX, currentY, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      animFrameRef.current = requestAnimationFrame(renderGraph);
    };

    renderGraph();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [progress, isPlaying, showThresholdLines]);

  const temp = telemetry.tempAnomaly;

  return (
    <div className="glass-panel p-6 lg:p-8 rounded-3xl border border-cyan-500/50 space-y-6 shadow-2xl relative overflow-hidden bg-slate-950/90">
      <div className="absolute -right-20 -top-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header Banner */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-4 relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 shadow-lg">
            <Radio className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider block">
              Live Real-Time Sonification Dashboard
            </span>
            <h3 className="text-xl font-black text-white mt-0.5">{telemetry.title}</h3>
          </div>
        </div>

        {/* Live Audio Status & Threshold Line Toggle */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowThresholdLines((prev) => !prev)}
            className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-1.5 border ${
              showThresholdLines
                ? 'bg-cyan-500 text-slate-950 border-cyan-400 glow-cyan shadow-md'
                : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'
            }`}
            title="Toggle Policy Reference Threshold Lines"
          >
            <span>Threshold Lines: {showThresholdLines ? 'ON' : 'OFF (Clean View)'}</span>
          </button>

          {isPlaying && (
            <span className="flex items-center gap-1.5 text-xs font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-3 py-1.5 rounded-xl shadow-lg">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              Playback Active
            </span>
          )}
        </div>
      </div>

      {/* --- GRADUATED CLIMATE THRESHOLD ESCALATION BANNER --- */}
      {isPlaying && temp >= 1.0 && (
        <div className="relative z-10 transition-all duration-300">
          {temp >= 4.0 ? (
            <div className="p-3 rounded-2xl bg-purple-950/90 border border-purple-500/60 text-purple-200 flex items-center justify-between shadow-xl text-xs font-mono font-bold">
              <span className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-purple-400" />
                <span>⚡ CATASTROPHIC THRESHOLD: +4.0°C HIGH-EMISSIONS HORIZON EXCEEDED</span>
              </span>
              <span className="bg-purple-900/60 text-purple-300 px-2.5 py-1 rounded-xl">SSP5-8.5 Target</span>
            </div>
          ) : temp >= 2.0 ? (
            <div className="p-3 rounded-2xl bg-rose-950/80 border border-rose-500/50 text-rose-200 flex items-center justify-between shadow-lg text-xs font-mono font-bold">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-400" />
                <span>🚨 SEVERE IMPACT LIMIT: +2.0°C CRITICAL CLIMATE THRESHOLD BREACHED</span>
              </span>
              <span className="bg-rose-900/60 text-rose-300 px-2.5 py-1 rounded-xl">Severe Ecosystem Risk</span>
            </div>
          ) : temp >= 1.5 ? (
            <div className="p-3 rounded-2xl bg-orange-950/60 border border-orange-500/40 text-orange-200 flex items-center justify-between shadow-md text-xs font-mono font-bold">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                <span>⚠️ PARIS AGREEMENT TARGET: +1.5°C POLICY LIMIT REACHED</span>
              </span>
              <span className="bg-orange-900/60 text-orange-300 px-2.5 py-1 rounded-xl">Paris 1.5°C Limit</span>
            </div>
          ) : (
            <div className="p-2.5 rounded-2xl bg-slate-900/80 border border-slate-800 text-slate-300 flex items-center justify-between text-xs font-mono">
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400/80" />
                <span className="text-slate-300 font-medium">ℹ️ Modern Baseline Passed: <strong className="text-amber-400 font-bold">+1.0°C</strong> above pre-industrial</span>
              </span>
              <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded-lg text-[10px] font-mono">Historical Baseline</span>
            </div>
          )}
        </div>
      )}

      {/* Hero Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
        <div className="glass-panel p-4 rounded-2xl border border-slate-800 space-y-1">
          <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-cyan-400" /> TIMELINE POSITION
          </span>
          <div className="text-lg font-black text-white font-mono">{telemetry.yearLabel}</div>
        </div>

        <div className="glass-panel p-4 rounded-2xl border border-cyan-500/40 bg-cyan-950/20 space-y-1 shadow-lg">
          <span className="text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-wider flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5 text-cyan-400" /> {telemetry.metricName}
          </span>
          <div className="text-2xl font-black text-cyan-300 font-mono">{telemetry.metricValue}</div>
        </div>

        <div className="glass-panel p-4 rounded-2xl border border-amber-500/40 bg-amber-950/20 space-y-1 shadow-lg">
          <span className="text-[10px] font-mono font-bold text-amber-300 uppercase tracking-wider flex items-center gap-1">
            <Music className="w-3.5 h-3.5 text-amber-400" /> SONIFIED PITCH
          </span>
          <div className="text-lg font-black text-amber-300 font-mono">
            {telemetry.noteName} ({telemetry.freqHz} Hz)
          </div>
        </div>

        <div className="glass-panel p-4 rounded-2xl border border-purple-500/40 bg-purple-950/20 space-y-1 shadow-lg">
          <span className="text-[10px] font-mono font-bold text-purple-300 uppercase tracking-wider flex items-center gap-1">
            <Activity className="w-3.5 h-3.5 text-purple-400" /> MUSICAL SCALE MODE
          </span>
          <div className="text-xs font-bold text-purple-300 line-clamp-1 mt-1">{telemetry.scaleMode}</div>
        </div>
      </div>

      {/* Dynamic Animated Real-Time Trajectory Chart Canvas */}
      <div className="space-y-2 relative z-10">
        <div className="flex items-center justify-between text-xs font-mono text-slate-400">
          <span className="flex items-center gap-1.5 text-white font-bold">
            <Activity className="w-4 h-4 text-cyan-400" />
            <span>Dynamic Acoustic Trajectory with Climate Warning Lines</span>
          </span>
          <span>Timeline Progress: {Math.round(progress * 100)}%</span>
        </div>
        <canvas ref={canvasRef} width={1000} height={130} className="w-full h-32 bg-slate-950 rounded-2xl border border-slate-800 shadow-inner" />
      </div>

      {/* Interactive Timeline Scrubber Slider */}
      <div className="space-y-1.5 bg-slate-900/80 p-4 rounded-2xl border border-slate-800 relative z-10">
        <div className="flex items-center justify-between text-xs font-mono">
          <span className="text-slate-300 font-bold flex items-center gap-1.5">
            <Sliders className="w-4 h-4 text-cyan-400" />
            <span>Interactive Timeline Scrubber: Drag slider to jump to any data point</span>
          </span>
          <span className="text-cyan-300 font-mono font-bold">{Math.round(progress * 100)}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="1"
          step="0.001"
          value={progress}
          onChange={(e) => onScrubProgress(parseFloat(e.target.value))}
          className="w-full h-2.5 accent-cyan-400 bg-slate-950 rounded-lg cursor-pointer"
        />
      </div>

      {/* Active Synthesizer Patches Badge Bar */}
      <div className="flex items-center justify-between text-xs bg-slate-900/90 p-3.5 rounded-2xl border border-slate-800 flex-wrap gap-2 relative z-10">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="font-bold text-slate-300">Active Synth Patches:</span>
          <span className="text-slate-400 font-mono">{telemetry.synthPatches.join(' + ')}</span>
        </div>
        <div className="text-slate-400 font-mono text-[11px]">
          IPCC AR6 WGI Climate Warning Engine
        </div>
      </div>
    </div>
  );
};
